import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';

type Visibility = 'all' | 'factory' | 'user';

interface FactoryRef {
  id: string;
  name: string;
}

interface UserRef {
  id: string;
  name: string;
  email: string;
  organization_id?: string | null;
  organization?: { id: string; name: string } | null;
}

interface LoginQuote {
  id: string;
  quote_text: string;
  author_name: string;
  author_title: string | null;
  photo_url: string | null;
  visibility: Visibility;
  targetFactories: FactoryRef[];
  targetUsers: UserRef[];
  is_active: boolean;
  created_at: string;
}

interface QuoteForm {
  quoteText: string;
  authorName: string;
  authorTitle: string;
  photoPreview: string | null;
  photoFile: File | null;
  suggestedPhotoUrl: string | null;
  visibility: Visibility;
  targetFactoryIds: string[];
  targetUserIds: string[];
  isActive: boolean;
}

interface QuoteSuggestion {
  quote_text: string;
  author_name: string;
  author_title: string | null;
  photo_url: string | null;
}

const initials = (name: string) =>
  name.split(' ').filter(Boolean).map((n) => n[0]).join('').toUpperCase().substring(0, 2);

const emptyForm = (): QuoteForm => ({
  quoteText: '',
  authorName: '',
  authorTitle: '',
  photoPreview: null,
  photoFile: null,
  suggestedPhotoUrl: null,
  visibility: 'all',
  targetFactoryIds: [],
  targetUserIds: [],
  isActive: true,
});

export const LoginQuotesPanel: React.FC = () => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

  const [quotes, setQuotes] = useState<LoginQuote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [factories, setFactories] = useState<FactoryRef[]>([]);
  const [pickerUsers, setPickerUsers] = useState<UserRef[]>([]);

  const [statusFilter, setStatusFilter] = useState<'All' | 'Active' | 'Inactive'>('All');
  const [searchTerm, setSearchTerm] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<QuoteForm>(emptyForm());
  const [factorySearch, setFactorySearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingSuggestion, setIsFetchingSuggestion] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState<LoginQuote | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'info' | 'error'>('success');
  const triggerToast = (msg: string, type: 'success' | 'info' | 'error' = 'success') => {
    setToastMessage(msg);
    setToastType(type);
    setTimeout(() => setToastMessage(null), 4000);
  };

  const authHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchQuotes = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await fetch(`${baseUrl}/api/v1/login-quotes`, { headers: authHeaders() });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to fetch login quotes');
      setQuotes(data.data || []);
    } catch (err: any) {
      setLoadError(err.message || 'Failed to load login quotes');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPickers = async () => {
    try {
      const response = await fetch(`${baseUrl}/api/v1/login-quotes/pickers`, { headers: authHeaders() });
      const data = await response.json();
      if (response.ok && data.success) {
        setFactories(data.data.organizations || []);
        setPickerUsers(data.data.users || []);
      }
    } catch {
      // Pickers are non-critical — the visibility tabs just show empty lists if this fails.
    }
  };

  useEffect(() => {
    fetchQuotes();
    fetchPickers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddModal = () => {
    setIsEditing(false);
    setEditingId(null);
    setForm(emptyForm());
    setFactorySearch('');
    setUserSearch('');
    setShowModal(true);
  };

  const openEditModal = (quote: LoginQuote) => {
    setIsEditing(true);
    setEditingId(quote.id);
    setForm({
      quoteText: quote.quote_text,
      authorName: quote.author_name,
      authorTitle: quote.author_title || '',
      photoPreview: quote.photo_url,
      photoFile: null,
      suggestedPhotoUrl: null,
      visibility: quote.visibility,
      targetFactoryIds: quote.targetFactories.map((f) => f.id),
      targetUserIds: quote.targetUsers.map((u) => u.id),
      isActive: quote.is_active,
    });
    setFactorySearch('');
    setUserSearch('');
    setShowModal(true);
  };

  const handlePhotoPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((f) => ({ ...f, photoFile: file, photoPreview: URL.createObjectURL(file), suggestedPhotoUrl: null }));
  };

  const fetchSuggestion = async () => {
    setIsFetchingSuggestion(true);
    try {
      const response = await fetch(`${baseUrl}/api/v1/login-quotes/suggest`, { headers: authHeaders() });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to fetch a quote suggestion.');

      const suggestion: QuoteSuggestion = data.data;
      setForm((f) => ({
        ...f,
        quoteText: suggestion.quote_text,
        authorName: suggestion.author_name,
        authorTitle: suggestion.author_title || '',
        photoFile: null,
        photoPreview: suggestion.photo_url,
        suggestedPhotoUrl: suggestion.photo_url,
      }));
      triggerToast(
        suggestion.photo_url ? 'Suggestion loaded — review before saving.' : 'Suggestion loaded, but no photo was found for this author.',
        'success'
      );
    } catch (err: any) {
      triggerToast(err.message || 'Failed to fetch a quote suggestion.', 'error');
    } finally {
      setIsFetchingSuggestion(false);
    }
  };

  const toggleFactory = (id: string) => {
    setForm((f) => ({
      ...f,
      targetFactoryIds: f.targetFactoryIds.includes(id)
        ? f.targetFactoryIds.filter((x) => x !== id)
        : [...f.targetFactoryIds, id],
    }));
  };

  const toggleUser = (id: string) => {
    setForm((f) => ({
      ...f,
      targetUserIds: f.targetUserIds.includes(id)
        ? f.targetUserIds.filter((x) => x !== id)
        : [...f.targetUserIds, id],
    }));
  };

  const buildFormData = (f: QuoteForm) => {
    const fd = new FormData();
    fd.append('quote_text', f.quoteText.trim());
    fd.append('author_name', f.authorName.trim());
    fd.append('author_title', f.authorTitle.trim());
    fd.append('visibility', f.visibility);
    fd.append('is_active', String(f.isActive));
    if (f.visibility === 'factory') fd.append('target_factory_ids', JSON.stringify(f.targetFactoryIds));
    if (f.visibility === 'user') fd.append('target_user_ids', JSON.stringify(f.targetUserIds));
    if (f.photoFile) {
      fd.append('photo', f.photoFile);
    } else if (f.suggestedPhotoUrl) {
      fd.append('photo_source_url', f.suggestedPhotoUrl);
    }
    return fd;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.quoteText.trim() || !form.authorName.trim()) {
      triggerToast('Quote text and author name are required.', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const url = isEditing && editingId
        ? `${baseUrl}/api/v1/login-quotes/${editingId}`
        : `${baseUrl}/api/v1/login-quotes`;
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: buildFormData(form),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to save quote');

      if (isEditing) {
        setQuotes((prev) => prev.map((q) => (q.id === data.data.id ? data.data : q)));
        triggerToast('Quote updated.', 'success');
      } else {
        setQuotes((prev) => [data.data, ...prev]);
        triggerToast('Quote added.', 'success');
      }
      setShowModal(false);
    } catch (err: any) {
      triggerToast(err.message || 'Failed to save quote', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleActive = async (quote: LoginQuote) => {
    const nextActive = !quote.is_active;
    setQuotes((prev) => prev.map((q) => (q.id === quote.id ? { ...q, is_active: nextActive } : q)));
    try {
      const fd = new FormData();
      fd.append('is_active', String(nextActive));
      const response = await fetch(`${baseUrl}/api/v1/login-quotes/${quote.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: fd,
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to update status');
      setQuotes((prev) => prev.map((q) => (q.id === quote.id ? data.data : q)));
    } catch (err: any) {
      setQuotes((prev) => prev.map((q) => (q.id === quote.id ? { ...q, is_active: quote.is_active } : q)));
      triggerToast(err.message || 'Failed to update status', 'error');
    }
  };

  const confirmDeleteQuote = async () => {
    if (!quoteToDelete) return;
    setIsDeleting(true);
    try {
      const response = await fetch(`${baseUrl}/api/v1/login-quotes/${quoteToDelete.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to remove quote');
      setQuotes((prev) => prev.filter((q) => q.id !== quoteToDelete.id));
      triggerToast('Quote removed.', 'info');
      setQuoteToDelete(null);
    } catch (err: any) {
      triggerToast(err.message || 'Failed to remove quote', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredQuotes = quotes.filter((q) => {
    const matchesStatus = statusFilter === 'All' || (statusFilter === 'Active') === q.is_active;
    const matchesSearch =
      q.quote_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.author_name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const filteredFactoryOptions = factories.filter((f) => f.name.toLowerCase().includes(factorySearch.toLowerCase()));
  const filteredUserOptions = pickerUsers.filter((u) =>
    u.name.toLowerCase().includes(userSearch.toLowerCase()) || u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  const visibilityBadge = (quote: LoginQuote) => {
    if (quote.visibility === 'all') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-100">
          <span className="material-symbols-outlined text-[12px]">public</span>
          Everyone
        </span>
      );
    }
    if (quote.visibility === 'factory') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700 border border-purple-100">
          <span className="material-symbols-outlined text-[12px]">factory</span>
          {quote.targetFactories.length} {quote.targetFactories.length === 1 ? 'Factory' : 'Factories'}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">
        <span className="material-symbols-outlined text-[12px]">person</span>
        {quote.targetUsers.length} {quote.targetUsers.length === 1 ? 'User' : 'Users'}
      </span>
    );
  };

  return (
    <div className="flex flex-col gap-8 animate-fade-in font-sans relative">
      {/* Toast */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20, x: 20 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, y: -20, x: 20 }}
            className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-4 py-3.5 rounded-xl border shadow-lg ${
              toastType === 'success' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
              toastType === 'info' ? 'bg-blue-50 text-blue-800 border-blue-200' :
              'bg-rose-50 text-rose-800 border-rose-200'
            }`}
          >
            <span className="material-symbols-outlined text-[20px]">
              {toastType === 'success' ? 'check_circle' : toastType === 'info' ? 'info' : 'warning'}
            </span>
            <span className="text-xs font-semibold">{toastMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2.5">
            Login Quotes
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-100">
              <span className="material-symbols-outlined text-[12px]">workspace_premium</span>
              Premium
            </span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">Curate inspirational quotes shown to admins and app users right after login.</p>
        </div>

        <button
          onClick={openAddModal}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#136bce] text-white hover:bg-blue-700 rounded-xl text-xs font-bold transition-all shadow-sm shadow-blue-500/25"
        >
          <span className="material-symbols-outlined text-[16px]">add_circle</span>
          Add Quote
        </button>
      </div>

      {/* KPI stats bar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm flex items-center justify-between hover:shadow-md transition-all">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Total Quotes</span>
            <span className="text-2xl font-extrabold text-slate-950 font-mono">{quotes.length}</span>
          </div>
          <div className="w-10 h-10 bg-blue-50 rounded-xl border border-blue-100 flex items-center justify-center text-blue-600">
            <span className="material-symbols-outlined text-lg">format_quote</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm flex items-center justify-between hover:shadow-md transition-all">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Active</span>
            <span className="text-2xl font-extrabold text-emerald-600 font-mono">{quotes.filter((q) => q.is_active).length}</span>
          </div>
          <div className="w-10 h-10 bg-emerald-50 rounded-xl border border-emerald-100 flex items-center justify-center text-emerald-600">
            <span className="material-symbols-outlined text-lg">visibility</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-sm flex items-center justify-between hover:shadow-md transition-all">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Targeted (Not Everyone)</span>
            <span className="text-2xl font-extrabold text-purple-700 font-mono">{quotes.filter((q) => q.visibility !== 'all').length}</span>
          </div>
          <div className="w-10 h-10 bg-purple-100 rounded-xl border border-purple-100 flex items-center justify-center text-purple-700">
            <span className="material-symbols-outlined text-lg">filter_alt</span>
          </div>
        </div>
      </div>

      {/* Quote cards */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center bg-slate-100 p-0.5 rounded-xl border border-slate-200/50">
            {(['All', 'Active', 'Inactive'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all ${
                  statusFilter === s ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                {s === 'All' ? 'All States' : s}
              </button>
            ))}
          </div>

          <div className="relative max-w-xs w-full">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">search</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search quotes or authors..."
              className="w-full bg-slate-50 text-xs rounded-xl border border-slate-200 pl-10 pr-4 py-2.5 text-slate-800 placeholder-slate-400 focus:bg-white focus:ring-1 focus:ring-[#136bce] focus:border-[#136bce] transition-all focus:outline-none"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="py-12 text-center text-slate-400 text-xs">
            <span className="material-symbols-outlined text-[28px] text-slate-300 block mb-2 animate-spin">progress_activity</span>
            Loading login quotes…
          </div>
        ) : loadError ? (
          <div className="py-12 text-center text-xs">
            <span className="material-symbols-outlined text-[32px] text-rose-300 block mb-2">error</span>
            <p className="text-rose-600 font-semibold mb-3">{loadError}</p>
            <button
              onClick={fetchQuotes}
              className="px-4 py-1.5 bg-[#136bce] text-white rounded-lg text-[11px] font-bold hover:bg-blue-700 transition-all inline-flex items-center gap-1.5"
            >
              <span className="material-symbols-outlined text-[14px]">refresh</span>
              Retry
            </button>
          </div>
        ) : filteredQuotes.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-xs">
            <span className="material-symbols-outlined text-[36px] text-slate-300 block mb-2">format_quote</span>
            No quotes match your criteria.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredQuotes.map((quote) => (
              <div key={quote.id} className="bg-slate-50/50 border border-slate-200/80 rounded-2xl p-5 flex flex-col gap-4 hover:border-[#136bce]/40 transition-all">
                <div className="flex items-start justify-between gap-2">
                  {visibilityBadge(quote)}
                  <button
                    onClick={() => toggleActive(quote)}
                    title="Click to toggle status"
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border transition-all ${
                      quote.is_active
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100/50'
                        : 'bg-rose-50 text-rose-700 border-rose-100 hover:bg-rose-100/50'
                    }`}
                  >
                    <span className={`w-1 h-1 rounded-full ${quote.is_active ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                    {quote.is_active ? 'Active' : 'Inactive'}
                  </button>
                </div>

                <p className="text-sm italic text-slate-800 leading-relaxed flex-1">&ldquo;{quote.quote_text}&rdquo;</p>

                <div className="flex items-center gap-2.5 pt-3 border-t border-slate-200/80">
                  {quote.photo_url ? (
                    <img src={quote.photo_url} alt={quote.author_name} className="w-9 h-9 rounded-full object-cover border border-slate-200" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-slate-100 border border-slate-200/60 flex items-center justify-center font-bold text-[#136bce] uppercase text-[11px]">
                      {initials(quote.author_name)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-slate-900 truncate">{quote.author_name}</p>
                    <p className="text-[10px] text-slate-500 truncate">{quote.author_title || '—'}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEditModal(quote)}
                      className="p-1.5 h-8 w-8 text-[#136bce] hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors border border-transparent hover:border-blue-100 flex items-center justify-center"
                      title="Edit quote"
                    >
                      <span className="material-symbols-outlined text-[16px]">edit</span>
                    </button>
                    <button
                      onClick={() => setQuoteToDelete(quote)}
                      className="p-1.5 h-8 w-8 text-slate-450 hover:text-red-650 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100 flex items-center justify-center"
                      title="Remove quote"
                    >
                      <span className="material-symbols-outlined text-[16px]">delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ADD / EDIT QUOTE MODAL */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-3xl border border-slate-200 w-full max-w-5xl shadow-2xl flex flex-col max-h-[92dvh] overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 pt-6 pb-3.5 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-[#136bce]">{isEditing ? 'edit_square' : 'format_quote'}</span>
                  <h3 className="text-lg font-bold text-slate-900">{isEditing ? 'Edit Login Quote' : 'Add Login Quote'}</h3>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <form onSubmit={handleSave} className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[1.5fr_1fr]">
                {/* LEFT: FORM */}
                <div className="flex flex-col gap-5 p-6 overflow-y-auto">

                  {/* SECTION: QUOTE */}
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-1">
                      <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[14px] text-[#136bce]">format_quote</span>
                        Quote
                      </span>
                      <button
                        type="button"
                        onClick={fetchSuggestion}
                        disabled={isFetchingSuggestion}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider text-[#136bce] bg-blue-50 hover:bg-blue-100 border border-blue-100 transition-all disabled:opacity-60 disabled:pointer-events-none"
                        title="Fetch a quote and author photo from the internet — fills the form below, saves nothing yet"
                      >
                        <span className={`material-symbols-outlined text-[13px] ${isFetchingSuggestion ? 'animate-spin' : ''}`}>
                          {isFetchingSuggestion ? 'progress_activity' : 'travel_explore'}
                        </span>
                        {isFetchingSuggestion ? 'Fetching…' : 'Get from Internet'}
                      </button>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                        Quote Text * <span className="normal-case font-medium text-slate-400">{form.quoteText.length} / 220</span>
                      </label>
                      <textarea
                        required
                        maxLength={220}
                        rows={3}
                        className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm italic text-slate-950 placeholder-slate-400 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none resize-none"
                        placeholder="e.g. Quality is everyone's responsibility."
                        value={form.quoteText}
                        onChange={(e) => setForm({ ...form, quoteText: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* SECTION: AUTHOR */}
                  <div className="flex flex-col gap-3.5">
                    <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5 border-b border-slate-100 pb-1">
                      <span className="material-symbols-outlined text-[14px] text-[#136bce]">badge</span>
                      Author
                    </span>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Author Name *</label>
                        <input
                          required
                          type="text"
                          className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                          placeholder="e.g. W. Edwards Deming"
                          value={form.authorName}
                          onChange={(e) => setForm({ ...form, authorName: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Title <span className="normal-case text-slate-400 font-medium">optional</span></label>
                        <input
                          type="text"
                          className="w-full h-10 rounded-xl border border-slate-200 px-3.5 py-2 text-xs placeholder-slate-400 text-slate-950 focus:border-[#136bce] focus:ring-1 focus:ring-[#136bce] focus:outline-none"
                          placeholder="e.g. Father of the Quality Movement"
                          value={form.authorTitle}
                          onChange={(e) => setForm({ ...form, authorTitle: e.target.value })}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-wider mb-1">Author Photo</label>
                      <div className="flex items-center gap-4">
                        {form.photoPreview ? (
                          <img src={form.photoPreview} alt="Preview" className="w-14 h-14 rounded-full object-cover border border-slate-200 flex-none" />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-slate-100 border border-slate-200/60 flex items-center justify-center font-bold text-[#136bce] uppercase text-sm flex-none">
                            {form.authorName ? initials(form.authorName) : '?'}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="flex-1 flex items-center justify-between gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-left hover:border-[#136bce] transition-colors"
                        >
                          <span className="flex flex-col">
                            <span className="text-xs font-bold text-slate-700">Click to upload a photo</span>
                            <span className="text-[10px] text-slate-400">JPG or PNG, square looks best</span>
                          </span>
                          <span className="material-symbols-outlined text-slate-400 text-lg">upload</span>
                        </button>
                        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoPick} />
                      </div>
                    </div>
                  </div>

                  {/* SECTION: VISIBILITY */}
                  <div className="flex flex-col gap-3 bg-slate-50 rounded-2xl p-4 border border-slate-200/80">
                    <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5 border-b border-slate-200/50 pb-1.5">
                      <span className="material-symbols-outlined text-[14px] text-[#136bce]">visibility</span>
                      Who should see this quote?
                    </span>

                    <div className="inline-flex bg-white p-0.5 rounded-xl border border-slate-200 self-start">
                      {([
                        { key: 'all', label: 'Everyone' },
                        { key: 'factory', label: 'Specific factories' },
                        { key: 'user', label: 'Specific admins & users' },
                      ] as const).map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setForm({ ...form, visibility: opt.key })}
                          className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                            form.visibility === opt.key ? 'bg-[#136bce] text-white shadow-sm' : 'text-slate-500 hover:text-slate-800'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    {form.visibility === 'factory' && (
                      <div className="flex flex-col gap-2.5">
                        <div className="relative">
                          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base">search</span>
                          <input
                            type="text"
                            value={factorySearch}
                            onChange={(e) => setFactorySearch(e.target.value)}
                            placeholder="Search factories..."
                            className="w-full bg-white text-xs rounded-lg border border-slate-200 pl-9 pr-3 py-2 text-slate-800 placeholder-slate-400 focus:ring-1 focus:ring-[#136bce] focus:border-[#136bce] focus:outline-none"
                          />
                        </div>
                        {factories.length === 0 ? (
                          <p className="text-[10px] text-slate-400 font-medium">Loading factories…</p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {filteredFactoryOptions.map((factory) => {
                              const selected = form.targetFactoryIds.includes(factory.id);
                              return (
                                <button
                                  key={factory.id}
                                  type="button"
                                  onClick={() => toggleFactory(factory.id)}
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border transition-all ${
                                    selected
                                      ? 'bg-purple-100 text-purple-700 border-purple-200'
                                      : 'bg-white text-slate-600 border-slate-200 hover:border-purple-200'
                                  }`}
                                >
                                  <span className="material-symbols-outlined text-[13px]">{selected ? 'check_circle' : 'factory'}</span>
                                  {factory.name}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {form.targetFactoryIds.length === 0 && (
                          <p className="text-[10px] text-slate-400 font-medium">No factories selected yet.</p>
                        )}
                      </div>
                    )}

                    {form.visibility === 'user' && (
                      <div className="flex flex-col gap-2.5">
                        <div className="relative">
                          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-base">search</span>
                          <input
                            type="text"
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                            placeholder="Search by name or email..."
                            className="w-full bg-white text-xs rounded-lg border border-slate-200 pl-9 pr-3 py-2 text-slate-800 placeholder-slate-400 focus:ring-1 focus:ring-[#136bce] focus:border-[#136bce] focus:outline-none"
                          />
                        </div>
                        {pickerUsers.length === 0 ? (
                          <p className="text-[10px] text-slate-400 font-medium">Loading admins & users…</p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {filteredUserOptions.map((user) => {
                              const selected = form.targetUserIds.includes(user.id);
                              return (
                                <button
                                  key={user.id}
                                  type="button"
                                  onClick={() => toggleUser(user.id)}
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border transition-all ${
                                    selected
                                      ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                      : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-200'
                                  }`}
                                >
                                  <span className="material-symbols-outlined text-[13px]">{selected ? 'check_circle' : 'person'}</span>
                                  {user.name}{user.organization?.name ? ` — ${user.organization.name}` : ''}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {form.targetUserIds.length === 0 && (
                          <p className="text-[10px] text-slate-400 font-medium">No admins or users selected yet.</p>
                        )}
                      </div>
                    )}

                    {form.visibility === 'all' && (
                      <p className="text-[10px] text-slate-400 font-medium">Visible to every admin and app user, on next login.</p>
                    )}
                  </div>

                  {/* SECTION: STATUS */}
                  <div className="flex items-center justify-between gap-4 pt-1">
                    <div>
                      <p className="text-xs font-bold text-slate-800">Active</p>
                      <p className="text-[10px] text-slate-500">Only active quotes are eligible to be shown at login.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, isActive: !form.isActive })}
                      aria-pressed={form.isActive}
                      className={`w-11 h-6 rounded-full relative transition-colors flex-none ${form.isActive ? 'bg-[#136bce]' : 'bg-slate-200'}`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.isActive ? 'translate-x-5' : ''}`}
                      />
                    </button>
                  </div>
                </div>

                {/* RIGHT: LIVE PREVIEW */}
                <div className="bg-slate-50 border-t lg:border-t-0 lg:border-l border-slate-200 p-6 overflow-y-auto flex flex-col gap-6">

                  {/* Dashboard preview */}
                  <div className="flex flex-col gap-2.5">
                    <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">Live Preview &middot; Dashboard login</span>
                    <div
                      className="rounded-2xl p-6 relative overflow-hidden text-white"
                      style={{ background: 'radial-gradient(120% 140% at 0% 0%, rgba(185,139,46,0.35), transparent 55%), linear-gradient(165deg, #1b2648, #10182f 70%)' }}
                    >
                      <span
                        className="absolute -top-4 left-3 text-[90px] leading-none"
                        style={{ color: 'rgba(217,178,92,0.28)', fontFamily: 'Georgia, serif' }}
                      >
                        &ldquo;
                      </span>
                      <p className="relative italic text-[15px] leading-relaxed mb-4">
                        &ldquo;{form.quoteText || 'Your quote will appear here.'}&rdquo;
                      </p>
                      <div className="h-px w-full mb-4" style={{ background: 'linear-gradient(90deg, rgba(217,178,92,0.6), rgba(217,178,92,0))' }} />
                      <div className="flex items-center gap-3">
                        {form.photoPreview ? (
                          <img src={form.photoPreview} alt="" className="w-9 h-9 rounded-full object-cover" />
                        ) : (
                          <div className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold" style={{ background: 'linear-gradient(145deg, #d9b25c, #6b7ea8)' }}>
                            {form.authorName ? initials(form.authorName) : '?'}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-xs font-bold truncate">{form.authorName || 'Author name'}</p>
                          <p className="text-[10px] truncate" style={{ color: 'rgba(242,239,230,0.65)' }}>{form.authorTitle || 'Author title'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Mobile app preview */}
                  <div className="flex flex-col gap-2.5">
                    <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">Live Preview &middot; Mobile app</span>
                    <div className="flex justify-center">
                      <div className="w-[190px] rounded-[22px] p-1.5" style={{ background: '#14161d' }}>
                        <div className="rounded-2xl overflow-hidden" style={{ background: '#10182f' }}>
                          <div
                            className="p-4 relative overflow-hidden text-white"
                            style={{ background: 'radial-gradient(120% 140% at 0% 0%, rgba(185,139,46,0.35), transparent 55%), linear-gradient(165deg, #1b2648, #10182f 70%)' }}
                          >
                            <span
                              className="absolute -top-2.5 left-2 text-[56px] leading-none"
                              style={{ color: 'rgba(217,178,92,0.28)', fontFamily: 'Georgia, serif' }}
                            >
                              &ldquo;
                            </span>
                            <p className="relative italic text-[12px] leading-relaxed mb-3">
                              &ldquo;{form.quoteText || 'Your quote will appear here.'}&rdquo;
                            </p>
                            <div className="h-px w-full mb-3" style={{ background: 'linear-gradient(90deg, rgba(217,178,92,0.6), rgba(217,178,92,0))' }} />
                            <div className="flex items-center gap-2">
                              {form.photoPreview ? (
                                <img src={form.photoPreview} alt="" className="w-7 h-7 rounded-full object-cover" />
                              ) : (
                                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold" style={{ background: 'linear-gradient(145deg, #d9b25c, #6b7ea8)' }}>
                                  {form.authorName ? initials(form.authorName) : '?'}
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="text-[10px] font-bold truncate">{form.authorName || 'Author name'}</p>
                                <p className="text-[9px] truncate" style={{ color: 'rgba(242,239,230,0.65)' }}>{form.authorTitle || 'Author title'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-[11px] text-slate-500 leading-relaxed">
                    Shown once per day, right after an eligible admin or app user signs in — on both the dashboard and the mobile app.
                  </p>
                </div>
              </form>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-5 py-2 bg-[#136bce] text-white hover:bg-blue-700 rounded-xl text-xs font-bold transition-all shadow-sm shadow-blue-500/20 disabled:opacity-60 disabled:pointer-events-none inline-flex items-center gap-2"
                >
                  {isSaving && <span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>}
                  {isEditing ? 'Save Changes' : 'Save Quote'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* DELETE CONFIRM MODAL */}
      {quoteToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
          <div
            className="relative inline-block transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8 text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-600 ring-4 ring-red-50/50">
                <span className="material-symbols-outlined text-[32px]">delete</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Delete Login Quote</h3>
              <p className="text-sm text-slate-500 mb-8">
                Are you sure you want to delete the quote by{' '}
                <span className="font-semibold text-slate-700">{quoteToDelete.author_name}</span>? This action cannot be undone.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => setQuoteToDelete(null)}
                  disabled={isDeleting}
                  className="flex-1 rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#136bce] focus:ring-offset-2 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteQuote}
                  disabled={isDeleting}
                  className="flex-1 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {isDeleting ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      Deleting...
                    </div>
                  ) : (
                    'Confirm Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
